import { useContext } from 'react'
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom'
import { guideHref, guideSheetSearch, ReadableGuidesContext } from '../../lib/guides'

/**
 * A "HOW … WORKS" LINK ON A WORKING PAGE (HLP-49, David 1 Nov).
 *
 * It opens the ONE section it names in the guide sheet, beside the work, by
 * putting the sheet's address in the page's own query (lib/guides.ts,
 * guideSheetSearch) - so it is a real link: Back closes the sheet, a reload
 * reopens it, a middle-click opens the page with the sheet in a new tab. The
 * shell (AppShell) reads the query and draws the sheet.
 *
 * Links INSIDE a guide, every link on the guides page and Ask One's source
 * chips are NOT this: they still navigate to #/help/guides. Only a working
 * page's "How … works" link is a GuideLink.
 *
 * Outside a router (the render suite mounts cards on their own) it falls back
 * to the plain guide route, which is still a true link.
 *
 * 353 (HLP-50): IT RENDERS ONLY FOR A SECTION THIS PERSON MAY READ - the
 * shell's guides() read (ReadableGuidesContext). Nothing while that read is
 * pending or failed, and nothing outside the shell: never a link to a refusal,
 * and a role guide's link is invisible to anyone the server would refuse.
 */
export function GuideLink({ guide, section, label }: { guide: string; section: string; label: string }) {
  const readable = useContext(ReadableGuidesContext)
  const inRouter = useInRouterContext()
  if (!readable || !readable.has(`${guide}/${section}`)) return null
  if (!inRouter) {
    return (
      <a className="cardlink" href={guideHref(guide, section)}>
        {label}
      </a>
    )
  }
  return <RoutedGuideLink guide={guide} section={section} label={label} />
}

function RoutedGuideLink({ guide, section, label }: { guide: string; section: string; label: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const search = guideSheetSearch(location.search, { guide, section })
  return (
    <a
      className="cardlink"
      href={`#${location.pathname}${search}`}
      data-guide-sheet={`${guide}/${section}`}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        e.preventDefault()
        // `guideSheet` in the history state says THIS entry was pushed by the
        // link, so closing can go Back rather than leave a second copy of the
        // page behind it (AppShell's closeSheet).
        navigate({ pathname: location.pathname, search }, { state: { guideSheet: true } })
      }}
    >
      {label}
    </a>
  )
}
